import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Info, AlertTriangle, Megaphone, Gift, Sparkles } from 'lucide-react';

// Re-show interval in milliseconds (6 hours)
const DISMISS_INTERVAL = 6 * 60 * 60 * 1000;

const AnnouncementCard = ({ announcement, onDismiss }) => {
 const [isVisible, setIsVisible] = useState(true);

 // Check if announcement was dismissed and if it should be shown again
 useEffect(() => {
 const dismissedAnnouncements = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '{}');
 const dismissedTime = dismissedAnnouncements[announcement.id];
 
 if (dismissedTime) {
 const now = Date.now();
 const timeSinceDismissed = now - dismissedTime;
 
 if (timeSinceDismissed < DISMISS_INTERVAL) {
 // Still within dismiss interval, keep hidden
 setIsVisible(false);
 
 // Set timeout to show again when interval expires
 const timeRemaining = DISMISS_INTERVAL - timeSinceDismissed;
 const timer = setTimeout(() => {
 setIsVisible(true);
 // Remove from dismissed list
 const updated = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '{}');
 delete updated[announcement.id];
 localStorage.setItem('dismissedAnnouncements', JSON.stringify(updated));
 }, timeRemaining);
 
 return () => clearTimeout(timer);
 } else {
 // Interval expired, remove from dismissed list and show
 delete dismissedAnnouncements[announcement.id];
 localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissedAnnouncements));
 setIsVisible(true);
 }
 }
 }, [announcement.id]);

 const getTypeConfig = (type) => {
 switch (type) {
 case 'important':
 return {
 icon: <AlertTriangle className="w-5 h-5" />,
 containerClass: 'bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200/60',
 iconClass: 'text-rose-500 bg-rose-100/80',
 textClass: 'text-rose-900',
 subtextClass: 'text-rose-700/80',
 badgeClass: 'bg-rose-100 text-rose-700 border-rose-200/50',
 closeClass: 'text-rose-400 hover:text-rose-600 hover:bg-rose-100/50'
 };
      case 'update':
        return {
          icon: <Sparkles className="w-5 h-5" />,
          containerClass: 'bg-gradient-to-r from-surface-muted to-surface border-border',
          iconClass: 'text-ink-secondary bg-surface-muted',
          textClass: 'text-ink',
          subtextClass: 'text-ink-secondary',
          badgeClass: 'bg-surface-muted text-ink border-border',
          closeClass: 'text-ink-tertiary hover:text-ink hover:bg-surface-muted',
        };
 case 'maintenance':
 return {
 icon: <AlertCircle className="w-5 h-5" />,
 containerClass: 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200/60',
 iconClass: 'text-amber-500 bg-amber-100/80',
 textClass: 'text-amber-900',
 subtextClass: 'text-amber-700/80',
 badgeClass: 'bg-amber-100 text-amber-700 border-amber-200/50',
 closeClass: 'text-amber-400 hover:text-amber-600 hover:bg-amber-100/50'
 };
 case 'promotion':
 return {
 icon: <Gift className="w-5 h-5" />,
          containerClass: 'bg-gradient-to-r from-surface-muted to-surface border-border',
          iconClass: 'text-ink bg-surface-muted',
          textClass: 'text-ink',
          subtextClass: 'text-ink-secondary',
          badgeClass: 'bg-surface-muted text-ink border-border',
          closeClass: 'text-ink-tertiary hover:text-ink hover:bg-surface-muted',
        };
      default: // general
        return {
          icon: <Megaphone className="w-5 h-5" />,
          containerClass: 'bg-gradient-to-r from-surface-muted to-surface border-border',
          iconClass: 'text-ink-secondary bg-surface-muted',
          textClass: 'text-ink',
          subtextClass: 'text-ink-secondary',
          badgeClass: 'bg-surface-muted text-ink border-border',
          closeClass: 'text-ink-tertiary hover:text-ink hover:bg-surface-muted',
        };
 }
 };

 const handleDismiss = () => {
 // Store dismiss time in localStorage
 const dismissedAnnouncements = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '{}');
 dismissedAnnouncements[announcement.id] = Date.now();
 localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissedAnnouncements));
 
 setIsVisible(false);
 
 if (onDismiss) {
 onDismiss(announcement.id);
 }
 };

 if (!isVisible) return null;

 const config = getTypeConfig(announcement.announcement_type || 'general');

 return (
 <div 
 className={`border rounded-xl p-4 mb-4 shadow-sm backdrop-blur-sm transition-all duration-300 ease-in-out hover:shadow-md ${config.containerClass}`}
 >
 <div className="flex items-start gap-3">
 {/* Icon */}
 <div className={`flex-shrink-0 p-2 rounded-lg ${config.iconClass}`}>
 {config.icon}
 </div>
 
 {/* Content */}
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2">
 <h3 className={`font-semibold text-sm ${config.textClass}`}>
 {announcement.title}
 </h3>
 
 {/* Close button */}
 <button
 onClick={handleDismiss}
 className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${config.closeClass}`}
 aria-label="Dismiss announcement"
 title="Dismiss for 6 hours"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 
 <p className={`text-sm leading-relaxed mt-1 ${config.subtextClass}`}>
 {announcement.content}
 </p>
 
 {announcement.announcement_type && (
 <span className={`inline-flex items-center mt-2.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize border ${config.badgeClass}`}>
 {announcement.announcement_type}
 </span>
 )}
 </div>
 </div>
 </div>
 );
};

export default AnnouncementCard;

